import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { cadsRoleButtonKeydown, cadsRoleButtonKeyup } from '../../a11y/cads-role-button-keyboard';
import { CadsHeadingDetailBitComponent } from '../../bits/cads-heading-detail-bit/cads-heading-detail-bit.component';
import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { CadsProcessingSpinnerComponent } from '../cads-processing-spinner/cads-processing-spinner.component';
import type { CadsProcessingSpinnerConfig } from '../cads-processing-spinner/cads-processing-spinner.config';

import { CadsMenuRowItemConfig } from './cads-menu-row-item.config';

/** Matches `symbol-defs` toggle viewBox 72×48 at 1:1 css px; keep in sync with `.cads-menu-row-item__switch` in SCSS. */
const _MENU_ROW_TOGGLE_ICON_WIDTH_PX = 72;
const _MENU_ROW_TOGGLE_ICON_HEIGHT_PX = 48;
/** Cropped compact sprites (`toggle-*-compact`); 56×32 art at ~0.91 scale ≈ iOS switch proportions. */
const _MENU_ROW_TOGGLE_COMPACT_ICON_WIDTH_PX = 51;
const _MENU_ROW_TOGGLE_COMPACT_ICON_HEIGHT_PX = 29;

/**
 * **Menu row:** optional leading bare icon, {@link CadsHeadingDetailBitComponent} title/subtitle, and a trailing accessory
 * (chevron, static text, text + chevron, compact, or toggle). Tappable variants mirror {@link CadsTapViewComponent}
 * press/focus behavior; **text** trailing is non-interactive; **toggle** uses an accessible switch and emits
 * {@link CadsMenuRowItemComponent.toggleChanged} only (not {@link CadsMenuRowItemComponent.tapped}). Omit
 * {@link CadsMenuRowItemConfig.menuRowLeading} for label-only rows; use {@link CadsMenuRowItemConfig.density} **`compact`**
 * for settings-style lists.
 *
 * @see {@link CadsMenuRowItemConfig} — `menuRowHeading`, optional `menuRowLeading`, and `menuRowTrailing`.
 */
@Component({
    selector: 'cads-menu-row-item',
    imports: [CadsHeadingDetailBitComponent, CadsIconViewBitComponent, CadsProcessingSpinnerComponent],
    templateUrl: './cads-menu-row-item.component.html',
    styleUrl: './cads-menu-row-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsMenuRowItemComponent {
  /** Row content and trailing variant (host supplies all strings; no i18n inside CADS v1). */
  readonly config = input.required<CadsMenuRowItemConfig>();

  /** Fired when the row is activated (chevron, textWithChevron, or compact). Not used for **text** or **toggle**. */
  readonly tapped = output<void>();

  /** Fired when the user toggles a **toggle** trailing; host updates `menuRowTrailing.isOn` on the next change detection cycle. */
  readonly toggleChanged = output<boolean>();

  readonly _trailing = computed(() => this.config().menuRowTrailing);

  readonly _isToggleRow = computed(() => this._trailing().kind === 'toggle');

  readonly _isStaticRow = computed(() => this._trailing().kind === 'text');

  readonly _isTappableRow = computed(() => {
    const k = this._trailing().kind;
    return k === 'chevron' || k === 'compact' || k === 'textWithChevron';
  });

  readonly _toggleIsOn = computed(() => {
    const t = this._trailing();
    return t.kind === 'toggle' && t.isOn;
  });

  readonly _toggleProcessing = computed(() => {
    const t = this._trailing();
    return t.kind === 'toggle' && (t.processing ?? false);
  });

  readonly _isCompact = computed(() => this.config().density === 'compact');

  readonly _switchAriaLabel = computed(() => this.config().menuRowHeading.title.trim());

  readonly trailingChevronBitConfig = computed(
    (): CadsIconViewBitConfig => ({
      icon: 'chevron_right',
      shape: 'icon',
      size: 'medium',
      collapsed: true,
      color: 'var(--cads-color-mid-gray)',
    }),
  );

  /**
   * Toggle glyphs (`toggle-on` / `toggle-off`) via {@link CadsIconViewBitConfig.iconWidthPx} / {@link CadsIconViewBitConfig.iconHeightPx}.
   */
  readonly toggleGlyphBitConfig = computed((): CadsIconViewBitConfig => {
    const t = this._trailing();
    const on = t.kind === 'toggle' && t.isOn;
    const compact = this._isCompact();
    return {
      icon: on
        ? compact
          ? 'toggle-on-compact'
          : 'toggle-on'
        : compact
          ? 'toggle-off-compact'
          : 'toggle-off',
      shape: 'icon',
      size: 'small',
      collapsed: true,
      iconWidthPx: compact ? _MENU_ROW_TOGGLE_COMPACT_ICON_WIDTH_PX : _MENU_ROW_TOGGLE_ICON_WIDTH_PX,
      iconHeightPx: compact ? _MENU_ROW_TOGGLE_COMPACT_ICON_HEIGHT_PX : _MENU_ROW_TOGGLE_ICON_HEIGHT_PX,
    };
  });

  /** {@link CadsProcessingSpinnerComponent} while toggle PATCH runs (same as {@link CadsAppButtonComponent}). */
  readonly toggleProcessingSpinnerConfig = computed((): CadsProcessingSpinnerConfig => ({
    size: 'default',
    accessibleLabel: '',
    color: 'var(--cads-color-blue)',
  }));

  readonly textWithChevronLabel = computed(() => {
    const t = this._trailing();
    return t.kind === 'textWithChevron' ? t.text : '';
  });

  readonly staticTrailingText = computed(() => {
    const t = this._trailing();
    return t.kind === 'text' ? t.text : '';
  });

  protected _onTappableActivate(): void {
    if (!this._isTappableRow()) {
      return;
    }
    this.tapped.emit();
  }

  protected _onTappableKeydown(event: KeyboardEvent): void {
    const tappable = this._isTappableRow();
    cadsRoleButtonKeydown(event, tappable, () => this._onTappableActivate());
  }

  protected _onTappableKeyup(event: KeyboardEvent): void {
    const tappable = this._isTappableRow();
    cadsRoleButtonKeyup(event, tappable, () => this._onTappableActivate());
  }

  protected _onToggleClick(event: Event): void {
    event.stopPropagation();
    const t = this._trailing();
    if (t.kind !== 'toggle' || t.processing) {
      return;
    }
    this.toggleChanged.emit(!t.isOn);
  }
}
